/**
 * @summary PAGES: This is any page for a microsite, https://domain.com/content/{slug}
 * This points to wordpress Pages content types
 */
import React from 'react';
import PageContainer from 'components/global/PageContainer';
import PageSectionLayout from 'components/global/PageSectionLayout';
import { pagePropsType } from 'utils/propTypes';
import getContentPageProps from 'app-requests/content-type-loaders/content-page-props';
import { getQuestionnaires } from 'app-requests/triadmsRequests';
import { getQuestionBank } from 'app-requests/triadms-apis/getQuestionBank';
import handleServerRedirect, {
  handleMaintenanceRedirect,
  redirectToStateSpecificPrivacyPolicy,
} from 'utils/handleServerRedirect';
import { APIError } from 'utils/errors/APIError';
import { APITransformError } from 'utils/errors/APITransformError';
import { LogError } from 'utils/logging/LogError';
import isDevelopment from 'utils/isDevelopment';
import { notFoundRedirect, SITE_TYPE, QUERY_PARAMS } from 'consts';
import { getGeolocationFromRequest } from 'utils/generalUtils';

function ContentPage(props) {
  const {
    pageProps: { pageSections, sidebarSections, sidebarRange, ...globalPageProps },
  } = props;

  return (
    <PageContainer pageProps={globalPageProps}>
      <PageSectionLayout
        pageSections={pageSections}
        sidebarSections={sidebarSections}
        sidebarRange={sidebarRange}
        isLanding
      />
    </PageContainer>
  );
}

export async function getServerSideProps(context) {
  try {
    const { req, res, query } = context;
    const domain = req.headers.host;
    const geoLocation = getGeolocationFromRequest(req);

    const stateSpecificPrivacyPolicy = redirectToStateSpecificPrivacyPolicy(
      geoLocation,
      context
    );

    if (stateSpecificPrivacyPolicy) {
      query[QUERY_PARAMS.CONTENT_SLUG] = stateSpecificPrivacyPolicy;
    }
    const pageProps = await getContentPageProps({ req, res, query });

    let questionBankMap;
    let questionnaires = {};

    if (
      pageProps.meta.siteType === SITE_TYPE.CLICK_PORTAL ||
      pageProps.meta.siteType === SITE_TYPE.MICRO_PORTAL
    ) {
      questionBankMap = await getQuestionBank(req.redisHelpers, req.cookies);
      questionnaires = await getQuestionnaires({
        questionBankMap,
        domain,
        schoolCode: pageProps.meta.schoolCode,
        formSettings: pageProps.formSettings.questionnaires,
        redisHelpers: req.redisHelpers,
      });
    }

    return {
      props: {
        ...pageProps,
        questionnaire: {},
        questionnaires,
      },
    };
  } catch (error) {
    if (error.statusCode === 404) {
      handleServerRedirect(notFoundRedirect, context);
      return { props: {} };
    }

    if (error instanceof APIError) {
      LogError(error, {
        statusCode: error.statusCode,
        endpoint: error.endpoint,
        route: 'content/[pages]',
      });
    } else if (error instanceof APITransformError) {
      LogError(error, {
        method: error.method,
        route: 'content/[pages]',
      });
    } else {
      LogError(error, {
        route: 'content/[pages]',
      });
    }

    if (isDevelopment()) {
      throw error;
    } else {
      handleMaintenanceRedirect(context, error);
    }
    return { props: {} };
  }
}

ContentPage.propTypes = {
  pageProps: pagePropsType.isRequired,
};

export default ContentPage;
